/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from "react";
import AuthAction from "@/src/components/Auth/AuthAction";
import DialogModal from "@/src/components/DialogModal/DialogModal";
import { AuthActionType } from "@/src/types/AuthActionType";
import { translate } from "@/src/services/translations/translations";
import { NavigateProps } from "react-router";

type AuthDialogProviderView = {
  type: AuthActionType;
  showPostfix?: boolean;
  initialEmail?: string;
  redirectTo?: NavigateProps["to"] | null;
} | null;

const AuthDialogContext = createContext<{
  view: AuthDialogProviderView;
  setView: Dispatch<SetStateAction<AuthDialogProviderView>>;
} | null>(null);

export type AuthDialogContextProviderProps = {
  children: ReactNode;
};

export const useAuthDialog = () => {
  const authDialogContext = useContext(AuthDialogContext);

  if (!authDialogContext) {
    throw new Error("useAuthDialog can only be used inside AuthDialogProvider");
  }

  return authDialogContext;
};

export default function AuthDialogProvider({ children }: AuthDialogContextProviderProps) {
  const [view, setView] = useState<AuthDialogProviderView>(null);

  const handleClose = () => {
    setView(null);
  };

  const handleActionTypeChange = (actionType: AuthActionType) => {
    setView((prevView) => ({
      ...prevView,
      type: actionType,
    }));
  };

  return (
    <AuthDialogContext value={{ view, setView }}>
      {children}
      <DialogModal open={!!view} onClose={handleClose} title={translate("auth." + view?.type, {}, "")} titleVariant={"h5"} variant={"clean"} maxWidth={"sm"}>
        {/* Needs the view option as key, to be able to update the component view from parent comp because it sets it as default state after remount */}
        <AuthAction
          actionType={view?.type}
          onActionTypeChange={handleActionTypeChange}
          showPostfix={view?.showPostfix}
          redirectTo={view?.redirectTo}
          initialEmail={view?.initialEmail}
        />
      </DialogModal>
    </AuthDialogContext>
  );
}
